// client/src/hooks/useVendas.js - VERSÃO CORRIGIDA

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';

const VENDAS_QUERY_KEY = 'vendas';

// ✅ CORREÇÃO: Função auxiliar melhorada para construir parâmetros de query
const buildVendasParams = (params = {}) => {
    const defaultParams = {
        page: 1,
        limit: 10,
        sort: '-createdAt',
        ...params
    };

    // ✅ CORREÇÃO: Remove parâmetros vazios, undefined ou null de forma mais segura
    Object.keys(defaultParams).forEach(key => {
        if (defaultParams[key] === undefined || defaultParams[key] === null || defaultParams[key] === '') {
            delete defaultParams[key];
        }
    });

    return defaultParams;
};

// ✅ CORREÇÃO: Função de processamento com tratamento de erro robusto
const processVendaData = (venda) => {
    if (!venda) {
        console.warn('⚠️ [processVendaData] Venda é null ou undefined');
        return null;
    }

    try {
        // ✅ CORREÇÃO: Verificação mais segura da data
        const dataVenda = venda.createdAt || venda.dataVenda || venda.updatedAt || new Date().toISOString();
        
        // ✅ CORREÇÃO: Processamento de cliente com fallbacks robustos
        let clienteProcessado = null;
        if (venda.cliente) {
            if (typeof venda.cliente === 'object' && venda.cliente._id) {
                if (venda.cliente.user) {
                    clienteProcessado = {
                        _id: venda.cliente._id,
                        nome: venda.cliente.user.name || venda.cliente.nome || venda.cliente.user?.nome || 'Cliente não informado',
                        email: venda.cliente.user.email || venda.cliente.email || '',
                        telefone: venda.cliente.user.telefone || venda.cliente.telefone || '',
                        dataNascimento: venda.cliente.user.dataNascimento,
                        cpf: venda.cliente.user.cpf,
                        user: venda.cliente.user,
                        ...venda.cliente
                    };
                } else {
                    clienteProcessado = {
                        _id: venda.cliente._id,
                        nome: venda.cliente.nome || venda.cliente.name || 'Cliente não informado',
                        email: venda.cliente.email || '',
                        telefone: venda.cliente.telefone || '',
                        ...venda.cliente
                    };
                }
            } else if (typeof venda.cliente === 'string') {
                clienteProcessado = {
                    _id: venda.cliente,
                    nome: 'Cliente não carregado',
                    email: '',
                    telefone: ''
                };
            }
        }

        // ✅ CORREÇÃO: Processamento de itens com validação robusta
        const itensProcessados = Array.isArray(venda.itens) 
            ? venda.itens.map(item => {
                if (!item) {
                    console.warn('⚠️ [processVendaData] Item da venda é null ou undefined');
                    return null;
                }

                let produtoProcessado = null;
                
                if (item.produto && typeof item.produto === 'object') {
                    produtoProcessado = {
                        _id: item.produto._id || 'unknown',
                        nome: item.produto.nome || 'Produto não informado',
                        descricao: item.produto.descricao || '',
                        preco: item.produto.preco || item.produto.valorFixo || item.produto.valor || item.produto.valorPorHora || 0,
                        ...(item.modelo === 'Servico' && {
                            duracaoSessaoEmMinutos: item.produto.duracaoSessaoEmMinutos,
                            categoria: item.produto.categoria
                        }),
                        ...(item.modelo === 'ItemVenda' && {
                            controlaEstoque: item.produto.controlaEstoque,
                            quantidadeEmEstoque: item.produto.quantidadeEmEstoque
                        }),
                        ...(item.modelo === 'PacoteCliente' && {
                            sessoesRestantes: item.produto.sessoesRestantes,
                            status: item.produto.status,
                            pacoteComprado: item.produto.pacoteComprado
                        }),
                        ...(item.modelo === 'Recurso' && {
                            tipo: item.produto.tipo,
                            valorPorHora: item.produto.valorPorHora
                        }),
                        ...item.produto
                    };
                } else {
                    produtoProcessado = { 
                        _id: item.produto || 'unknown',
                        nome: 'Produto não carregado',
                        descricao: '',
                        preco: 0 
                    };
                }

                // ✅ CORREÇÃO: Validação numérica robusta
                const precoUnitario = Number(item.precoUnitario) || 0;
                const quantidade = Number(item.quantidade) || 1;
                const desconto = Number(item.desconto) || 0;
                const total = Number(item.total) || (precoUnitario * quantidade - desconto);

                return {
                    ...item,
                    precoUnitario,
                    quantidade,
                    desconto,
                    total,
                    produto: produtoProcessado,
                    profissional: item.profissional && typeof item.profissional === 'object' ? {
                        _id: item.profissional._id,
                        name: item.profissional.name,
                        email: item.profissional.email
                    } : item.profissional
                };
            }).filter(Boolean) // Remove itens null
            : [];

        // ✅ CORREÇÃO: Cálculos financeiros com validação
        const totalCalculado = venda.total !== undefined && venda.total !== null 
            ? Number(venda.total) || 0
            : itensProcessados.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

        const subtotalCalculado = venda.subtotal !== undefined && venda.subtotal !== null
            ? Number(venda.subtotal) || 0
            : itensProcessados.reduce((sum, item) => sum + ((Number(item.precoUnitario) || 0) * (Number(item.quantidade) || 0)), 0);

        const descontoGeralCalculado = venda.descontoGeral !== undefined && venda.descontoGeral !== null
            ? Number(venda.descontoGeral) || 0
            : 0;

        const totalPago = venda.totalPago !== undefined ? Number(venda.totalPago) || 0 : 0;
        const saldoDevedor = venda.saldoDevedor !== undefined ? Number(venda.saldoDevedor) || 0 : (totalCalculado - totalPago);

        // ✅ CORREÇÃO: Processamento de origem
        const origemFormatada = venda.origemFormatada || 
            (venda.pedidoOrigem ? 'Pedido' : venda.agendamentoOrigem ? 'Agendamento' : 'Venda Direta');
        
        const veioDePedido = venda.veioDePedido !== undefined ? venda.veioDePedido : !!venda.pedidoOrigem;
        const veioDeAgendamento = venda.veioDeAgendamento !== undefined ? venda.veioDeAgendamento : !!venda.agendamentoOrigem;

        // ✅ CORREÇÃO: Processamento de pagamentos
        const pagamentosProcessados = Array.isArray(venda.pagamentos) 
            ? venda.pagamentos.map(pagamento => ({
                ...pagamento,
                valor: Number(pagamento.valor) || 0,
                metodo: pagamento.metodo && typeof pagamento.metodo === 'object' ? {
                    _id: pagamento.metodo._id,
                    nome: pagamento.metodo.nome,
                    tipo: pagamento.metodo.tipo
                } : pagamento.metodo,
                dataPagamento: pagamento.dataPagamento || null
            }))
            : [];

        // ✅ CORREÇÃO: Formatação de data com tratamento de erro
        let dataFormatada = 'N/A';
        let dataHoraFormatada = 'N/A';
        
        if (dataVenda) {
            try {
                const date = new Date(dataVenda);
                if (!isNaN(date.getTime())) {
                    dataFormatada = date.toLocaleDateString('pt-BR');
                    dataHoraFormatada = date.toLocaleString('pt-BR');
                }
            } catch (error) {
                console.warn('⚠️ [processVendaData] Erro ao formatar data:', error);
            }
        }

        const vendaProcessada = {
            ...venda,
            dataVenda,
            dataFormatada,
            dataHoraFormatada,
            cliente: clienteProcessado,
            itens: itensProcessados,
            pagamentos: pagamentosProcessados,
            subtotal: subtotalCalculado,
            descontoGeral: descontoGeralCalculado,
            total: totalCalculado,
            totalPago,
            saldoDevedor,
            statusVenda: venda.statusVenda || 'Aberta',
            statusPagamento: venda.statusPagamento || 'Pendente',
            origemFormatada,
            veioDePedido,
            veioDeAgendamento,
            _processed: true,
            _processedAt: new Date().toISOString()
        };

        console.log('✅ [processVendaData] Venda processada com sucesso:', {
            id: vendaProcessada._id,
            itens: vendaProcessada.itens.length,
            total: vendaProcessada.total
        });

        return vendaProcessada;

    } catch (error) {
        console.error('❌ [processVendaData] Erro crítico ao processar venda:', error, venda);
        
        // ✅ CORREÇÃO: Fallback robusto com estrutura mínima
        return {
            _id: venda._id || 'error-' + Date.now(),
            cliente: { 
                _id: venda.cliente?._id || 'error',
                nome: 'Erro ao carregar cliente',
                email: '',
                telefone: '' 
            },
            itens: [],
            pagamentos: [],
            subtotal: 0,
            descontoGeral: 0,
            total: 0,
            totalPago: 0,
            saldoDevedor: 0,
            statusVenda: 'Erro',
            statusPagamento: 'Erro',
            origemFormatada: 'Erro',
            veioDePedido: false,
            veioDeAgendamento: false,
            dataFormatada: 'Erro',
            dataHoraFormatada: 'Erro',
            _processed: false,
            _error: error.message,
            ...venda // Mantém dados originais para debug
        };
    }
};

// ✅ CORREÇÃO: Hook principal com tratamento de erro 500
export const useVendas = (params = {}, options = {}) => {
    const queryParams = buildVendasParams(params);

    return useQuery({
        queryKey: [VENDAS_QUERY_KEY, queryParams],
        queryFn: async () => {
            try {
                console.log('🔄 [useVendas] Buscando vendas com params:', queryParams);
                
                const response = await apiClient.get('/vendas', { 
                    params: queryParams,
                    timeout: 15000
                });
                
                // ✅ CORREÇÃO: Verificação robusta da resposta
                if (!response) {
                    console.error('❌ [useVendas] Resposta vazia da API');
                    throw new Error('Servidor não respondeu');
                }

                if (!response.data) {
                    console.warn('⚠️ [useVendas] Resposta sem data:', response);
                    return {
                        success: false,
                        data: [],
                        count: 0,
                        totalCount: 0,
                        pagination: {
                            page: queryParams.page,
                            limit: queryParams.limit,
                            totalPages: 0,
                            totalItems: 0
                        },
                        total: 0,
                        error: 'Resposta sem dados'
                    };
                }

                console.log('✅ [useVendas] Dados recebidos da API:', {
                    success: response.data?.success,
                    count: response.data?.count,
                    totalCount: response.data?.totalCount,
                    dataLength: response.data?.data?.length
                });

                if (!response.data.success) {
                    console.warn('⚠️ [useVendas] Resposta da API sem sucesso:', response.data);
                    return {
                        success: false,
                        data: [],
                        count: 0,
                        totalCount: 0,
                        pagination: {
                            page: queryParams.page,
                            limit: queryParams.limit,
                            totalPages: 0,
                            totalItems: 0
                        },
                        total: 0,
                        error: response.data.error || 'Operação não bem-sucedida'
                    };
                }

                // ✅ CORREÇÃO: Processamento seguro dos dados
                const rawData = Array.isArray(response.data.data) ? response.data.data : [];
                const processedData = rawData.map(processVendaData).filter(Boolean);

                const result = {
                    success: true,
                    data: processedData,
                    count: response.data.count || processedData.length,
                    totalCount: response.data.totalCount || response.data.count || processedData.length,
                    pagination: response.data.pagination || {
                        page: queryParams.page,
                        limit: queryParams.limit,
                        totalPages: Math.ceil((response.data.count || processedData.length) / queryParams.limit),
                        totalItems: response.data.count || processedData.length
                    },
                    total: response.data.total || response.data.count || processedData.length
                };

                console.log('✅ [useVendas] Dados processados com sucesso:', {
                    count: result.count,
                    dataLength: result.data.length,
                    pagination: result.pagination
                });
                return result;

            } catch (error) {
                console.error('❌ [useVendas] Erro ao buscar vendas:', error);
                
                // ✅ CORREÇÃO: Tratamento específico para erro 500
                if (error.response?.status === 500) {
                    console.error('🚨 [useVendas] ERRO 500 DO SERVIDOR - Verificar logs do backend');
                    return {
                        success: false,
                        data: [],
                        count: 0,
                        totalCount: 0,
                        pagination: {
                            page: queryParams.page,
                            limit: queryParams.limit,
                            totalPages: 0,
                            totalItems: 0
                        },
                        total: 0,
                        error: 'Erro interno do servidor. Tente novamente em alguns instantes.',
                        isServerError: true
                    };
                }

                // ✅ CORREÇÃO: Tratamento para outros erros de rede
                if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED') {
                    return {
                        success: false,
                        data: [],
                        count: 0,
                        totalCount: 0,
                        pagination: {
                            page: queryParams.page,
                            limit: queryParams.limit,
                            totalPages: 0,
                            totalItems: 0
                        },
                        total: 0,
                        error: 'Erro de conexão. Verifique sua internet.',
                        isNetworkError: true
                    };
                }

                return {
                    success: false,
                    data: [],
                    count: 0,
                    totalCount: 0,
                    pagination: {
                        page: queryParams.page,
                        limit: queryParams.limit,
                        totalPages: 0,
                        totalItems: 0
                    },
                    total: 0,
                    error: error.response?.data?.error || error.message || 'Erro desconhecido'
                };
            }
        },
        placeholderData: (previousData) => previousData,
        staleTime: 2 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
        retry: (failureCount, error) => {
            // ✅ CORREÇÃO: Lógica de retry mais inteligente
            if (error.response?.status >= 400 && error.response?.status < 500) {
                return false; // Não retry para erros do cliente
            }
            if (error.response?.status === 500) {
                return failureCount < 1; // Retry apenas uma vez para erro 500
            }
            return failureCount < 2; // Retry para outros erros
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        ...options
    });
};

// ✅ CORREÇÃO: Hook para venda específica com tratamento melhorado
export const useVenda = (vendaId, options = {}) => {
    return useQuery({
        queryKey: [VENDAS_QUERY_KEY, vendaId],
        queryFn: async () => {
            if (!vendaId) {
                console.warn('⚠️ [useVenda] vendaId não fornecido');
                return null;
            }
            
            try {
                console.log(`🔄 [useVenda] Buscando venda ${vendaId}`);
                
                const response = await apiClient.get(`/vendas/${vendaId}`);
                
                if (!response.data || !response.data.success) {
                    console.warn(`⚠️ [useVenda] Venda ${vendaId} não encontrada ou resposta inválida`);
                    return null;
                }

                const processedVenda = processVendaData(response.data.data);
                
                if (!processedVenda) {
                    console.error(`❌ [useVenda] Falha ao processar venda ${vendaId}`);
                    return null;
                }

                console.log(`✅ [useVenda] Venda ${vendaId} processada com sucesso`);
                return processedVenda;
            } catch (error) {
                console.error(`❌ [useVenda] Erro ao buscar venda ${vendaId}:`, error);
                
                if (error.response?.status === 404) {
                    console.warn(`⚠️ [useVenda] Venda ${vendaId} não encontrada`);
                    return null;
                }
                
                if (error.response?.status === 500) {
                    console.error(`🚨 [useVenda] Erro 500 ao buscar venda ${vendaId}`);
                }
                
                return null;
            }
        },
        enabled: !!vendaId && (options.enabled !== undefined ? options.enabled : true),
        staleTime: 3 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: (failureCount, error) => {
            if (error.response?.status === 404) return false;
            return failureCount < 2;
        },
        ...options
    });
};

// ✅ CORREÇÃO: Hook principal de mutations
export const useMutateVendas = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateVendasCache = (vendaId = null) => {
        console.log('🔄 [invalidateVendasCache] Invalidando cache', vendaId ? `para venda ${vendaId}` : '');
        
        try {
            const queriesToInvalidate = [
                [VENDAS_QUERY_KEY]
            ];

            if (vendaId) {
                queriesToInvalidate.push([VENDAS_QUERY_KEY, vendaId]);
            }

            queriesToInvalidate.forEach(queryKey => {
                queryClient.invalidateQueries({ queryKey });
            });

            // ✅ CORREÇÃO: Invalidar queries relacionadas de forma segura
            const relatedQueries = [
                ['clientes'], ['servicos'], ['itensVenda'], ['pacotes'], 
                ['recursos'], ['pedidos'], ['agendamentos'], ['reports', 'vendas'], ['comissoes']
            ];

            relatedQueries.forEach(queryKey => {
                try {
                    queryClient.invalidateQueries({ queryKey });
                } catch (error) {
                    console.warn(`⚠️ [invalidateVendasCache] Erro ao invalidar ${queryKey}:`, error);
                }
            });

            console.log('✅ [invalidateVendasCache] Cache invalidado com sucesso');
        } catch (error) {
            console.error('❌ [invalidateVendasCache] Erro ao invalidar cache:', error);
        }
    };

    // ✅ CORREÇÃO: Mutation para criar venda
    const createVenda = useMutation({
        mutationFn: async (vendaData) => {
            console.log('🔄 [createVenda] Criando nova venda:', {
                cliente: vendaData.cliente,
                itensCount: vendaData.itens?.length,
                origem: vendaData.pedidoOrigem ? 'Pedido' : vendaData.agendamentoOrigem ? 'Agendamento' : 'Direta'
            });
            
            // ✅ CORREÇÃO: Validação básica dos dados
            if (!vendaData.cliente) {
                throw new Error('Cliente é obrigatório');
            }
            
            if (!vendaData.itens || vendaData.itens.length === 0) {
                throw new Error('Pelo menos um item é obrigatório');
            }

            const response = await apiClient.post('/vendas', vendaData);
            return response.data;
        },
        onSuccess: (res) => {
            console.log('✅ [createVenda] Venda criada com sucesso:', {
                id: res.data?._id,
                status: res.data?.statusVenda,
                total: res.data?.total
            });
            invalidateVendasCache();
            setAlert('Venda criada com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            console.error('❌ [createVenda] Erro ao criar venda:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               err.message || 
                               'Erro ao criar venda';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    // ✅ CORREÇÃO: Mutation para atualizar venda
    const updateVenda = useMutation({
        mutationFn: async ({ id, vendaData }) => {
            console.log(`🔄 [updateVenda] Atualizando venda ${id}:`, {
                statusVenda: vendaData.statusVenda,
                statusPagamento: vendaData.statusPagamento,
                pagamentosCount: vendaData.pagamentos?.length,
                observacoes: vendaData.observacoes
            });
            
            if (!id) {
                throw new Error('ID da venda é obrigatório');
            }

            try {
                const vendaAtual = await apiClient.get(`/vendas/${id}`);
                const temAgendamentoOrigem = vendaAtual.data?.data?.agendamentoOrigem;
                
                let payload = { ...vendaData };
                
                if (temAgendamentoOrigem) {
                    console.warn(`⚠️ [updateVenda] Venda ${id} tem agendamentoOrigem - protegendo dados sensíveis`);
                    
                    delete payload.pagamentos;
                    delete payload.total;
                    delete payload.subtotal;
                    delete payload.descontoGeral;
                    
                    if (vendaAtual.data?.data?.statusVenda === 'Finalizada' && vendaData.statusVenda === 'Aberta') {
                        throw new Error('Não é possível reabrir venda finalizada via agendamento. Reabra o agendamento primeiro.');
                    }
                }
                
                const response = await apiClient.put(`/vendas/${id}`, payload);
                return response.data;
            } catch (error) {
                console.error(`❌ [updateVenda] Erro na verificação da venda ${id}:`, error);
                throw error;
            }
        },
        onSuccess: (res, variables) => {
            console.log(`✅ [updateVenda] Venda ${variables.id} atualizada com sucesso:`, {
                statusVenda: res.data?.statusVenda,
                statusPagamento: res.data?.statusPagamento
            });
            invalidateVendasCache(variables.id);
            setAlert('Venda atualizada com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            console.error('❌ [updateVenda] Erro ao atualizar venda:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               err.message ||
                               'Erro ao atualizar venda';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });
    
    // ✅ CORREÇÃO: Mutation para deletar venda
    const deleteVenda = useMutation({
        mutationFn: async (id) => {
            if (!id) {
                throw new Error('ID da venda é obrigatório');
            }

            console.log(`🔄 [deleteVenda] Deletando venda ${id}`);
            const response = await apiClient.delete(`/vendas/${id}`);
            return response.data;
        },
        onSuccess: (res, id) => {
            console.log(`✅ [deleteVenda] Venda ${id} deletada com sucesso`);
            invalidateVendasCache(id);
            setAlert('Venda cancelada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error('❌ [deleteVenda] Erro ao deletar venda:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               'Erro ao cancelar venda';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    // ✅ CORREÇÃO: Mutation para restaurar venda
    const restaurarVenda = useMutation({
        mutationFn: async (id) => {
            if (!id) {
                throw new Error('ID da venda é obrigatório');
            }

            console.log(`🔄 [restaurarVenda] Restaurando venda ${id}`);
            const response = await apiClient.put(`/vendas/${id}/restaurar`);
            return response.data;
        },
        onSuccess: (res, id) => {
            console.log(`✅ [restaurarVenda] Venda ${id} restaurada com sucesso`);
            invalidateVendasCache(id);
            setAlert('Venda restaurada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error('❌ [restaurarVenda] Erro ao restaurar venda:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               'Erro ao restaurar venda';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    // ✅ CORREÇÃO: Mutation para adicionar pagamento
    const adicionarPagamento = useMutation({
        mutationFn: async ({ id, pagamentoData }) => {
            if (!id) {
                throw new Error('ID da venda é obrigatório');
            }

            console.log(`🔄 [adicionarPagamento] Adicionando pagamento à venda ${id}:`, {
                metodo: pagamentoData.metodo,
                valor: pagamentoData.valor,
                status: pagamentoData.status
            });
            
            try {
                const vendaAtual = await apiClient.get(`/vendas/${id}`);
                const temAgendamentoOrigem = vendaAtual.data?.data?.agendamentoOrigem;
                
                if (temAgendamentoOrigem) {
                    console.warn(`⚠️ [adicionarPagamento] Venda ${id} tem agendamentoOrigem - use o agendamento para pagamentos`);
                    throw new Error('Para adicionar pagamentos em vendas de agendamento, use a função de pagamento do agendamento.');
                }
                
                const response = await apiClient.post(`/vendas/${id}/pagamentos`, pagamentoData);
                return response.data;
            } catch (error) {
                console.error(`❌ [adicionarPagamento] Erro ao verificar venda ${id}:`, error);
                throw error;
            }
        },
        onSuccess: (res, variables) => {
            console.log(`✅ [adicionarPagamento] Pagamento adicionado à venda ${variables.id} com sucesso`);
            invalidateVendasCache(variables.id);
            setAlert('Pagamento adicionado com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            console.error('❌ [adicionarPagamento] Erro ao adicionar pagamento:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               'Erro ao adicionar pagamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    // ✅ CORREÇÃO: Mutation para estornar pagamento
    const estornarPagamento = useMutation({
        mutationFn: async ({ id, pagamentoId }) => {
            if (!id || !pagamentoId) {
                throw new Error('ID da venda e do pagamento são obrigatórios');
            }

            console.log(`🔄 [estornarPagamento] Estornando pagamento ${pagamentoId} da venda ${id}`);
            
            try {
                const vendaAtual = await apiClient.get(`/vendas/${id}`);
                const temAgendamentoOrigem = vendaAtual.data?.data?.agendamentoOrigem;
                
                if (temAgendamentoOrigem) {
                    console.warn(`⚠️ [estornarPagamento] Venda ${id} tem agendamentoOrigem - use o agendamento para estornos`);
                    throw new Error('Para estornar pagamentos em vendas de agendamento, use a função de estorno do agendamento.');
                }
                
                const response = await apiClient.put(`/vendas/${id}/pagamentos/${pagamentoId}/estornar`);
                return response.data;
            } catch (error) {
                console.error(`❌ [estornarPagamento] Erro ao verificar venda ${id}:`, error);
                throw error;
            }
        },
        onSuccess: (res, variables) => {
            console.log(`✅ [estornarPagamento] Pagamento ${variables.pagamentoId} estornado com sucesso`);
            invalidateVendasCache(variables.id);
            setAlert('Pagamento estornado com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            console.error('❌ [estornarPagamento] Erro ao estornar pagamento:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               'Erro ao estornar pagamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    // ✅ CORREÇÃO: Mutation para criar venda a partir de pedido
    const criarVendaDePedido = useMutation({
        mutationFn: async (pedidoId) => {
            if (!pedidoId) {
                throw new Error('ID do pedido é obrigatório');
            }

            console.log(`🔄 [criarVendaDePedido] Criando venda a partir do pedido ${pedidoId}`);
            
            try {
                const pedidoResponse = await apiClient.get(`/pedidos/${pedidoId}`);
                const pedido = pedidoResponse.data.data;
                
                if (!pedido) {
                    throw new Error('Pedido não encontrado');
                }
                
                if (pedido.vendaRelacionada) {
                    throw new Error('Este pedido já possui uma venda vinculada');
                }
                
                const vendaData = {
                    pedidoOrigem: pedidoId,
                    cliente: pedido.cliente?._id || pedido.cliente,
                    unidade: pedido.itens?.[0]?.unidade?._id || pedido.itens?.[0]?.unidade || null,
                    profissionalPrincipal: pedido.profissionalResponsavel?._id || pedido.profissionalResponsavel,
                    itens: pedido.itens.map(item => ({
                        produto: item.produto?._id || item.produto,
                        modelo: item.modelo,
                        descricao: item.descricao,
                        quantidade: item.quantidade,
                        precoUnitario: item.precoUnitario,
                        desconto: item.desconto || 0,
                        total: item.total,
                        profissional: item.profissional?._id || item.profissional
                    })),
                    subtotal: pedido.subtotal,
                    descontoGeral: pedido.desconto || 0,
                    total: pedido.total,
                    observacoes: `Venda criada a partir do pedido ${pedido.numero}`
                };
                
                const response = await apiClient.post('/vendas', vendaData);
                return response.data;
            } catch (error) {
                console.error(`❌ [criarVendaDePedido] Erro ao processar pedido ${pedidoId}:`, error);
                throw error;
            }
        },
        onSuccess: (res) => {
            console.log('✅ [criarVendaDePedido] Venda criada a partir de pedido com sucesso:', res.data?._id);
            invalidateVendasCache();
            setAlert('Venda criada a partir do pedido com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            console.error('❌ [criarVendaDePedido] Erro ao criar venda a partir de pedido:', err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message ||
                               'Erro ao criar venda a partir do pedido';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    return { 
        createVenda, 
        updateVenda, 
        deleteVenda,
        restaurarVenda,
        adicionarPagamento,
        estornarPagamento,
        criarVendaDePedido
    };
};

// ✅ CORREÇÃO: Hook utilitário para dados formatados
export const useFormattedVendas = (params = {}) => {
    const { data: vendasData, isLoading, error, refetch, isFetching } = useVendas(params);

    const vendasArray = Array.isArray(vendasData?.data) ? vendasData.data : [];
    
    const formattedData = {
        vendas: vendasArray,
        pagination: vendasData?.pagination || {
            page: params.page || 1,
            limit: params.limit || 10,
            totalPages: 0,
            totalItems: 0
        },
        total: vendasData?.count || vendasData?.totalCount || 0,
        isLoading,
        isFetching,
        error,
        refetch,
        success: vendasData?.success !== false,
        isEmpty: vendasArray.length === 0,
        estatisticas: {
            totalVendas: vendasData?.count || 0,
            valorTotal: vendasArray.reduce((sum, venda) => sum + (venda.total || 0), 0),
            vendasFinalizadas: vendasArray.filter(v => v.statusVenda === 'Finalizada').length,
            vendasPendentes: vendasArray.filter(v => v.statusVenda === 'Aberta').length,
            vendasCanceladas: vendasArray.filter(v => v.statusVenda === 'Cancelada').length
        }
    };

    return formattedData;
};

// ✅ CORREÇÃO: Hook para busca de vendas
export const useVendasSearch = (searchTerm, options = {}) => {
    return useVendas({
        ...options,
        search: searchTerm
    }, {
        ...options,
        enabled: options.enabled !== false && !!searchTerm && searchTerm.length >= 2
    });
};

// ✅ CORREÇÃO: Hook para vendas por status
export const useVendasPorStatus = (status, options = {}) => {
    return useVendas({
        ...options,
        statusVenda: status
    }, {
        ...options,
        enabled: options.enabled !== false && !!status
    });
};

// ✅ CORREÇÃO: Hook para vendas por status de pagamento
export const useVendasPorStatusPagamento = (statusPagamento, options = {}) => {
    return useVendas({
        ...options,
        statusPagamento: statusPagamento
    }, {
        ...options,
        enabled: options.enabled !== false && !!statusPagamento
    });
};

// ✅ CORREÇÃO: Hook para vendas recentes
export const useVendasRecentes = (options = {}) => {
    const seteDiasAtras = new Date();
    seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);
    
    return useVendas({
        ...options,
        startDate: seteDiasAtras.toISOString().split('T')[0],
        sort: '-createdAt',
        limit: 20
    }, {
        ...options,
        staleTime: 1 * 60 * 1000 // Dados recentes ficam stale mais rápido
    });
};

// ✅ CORREÇÃO: Hook para vendas por período
export const useVendasPorPeriodo = (dataInicio, dataFim, options = {}) => {
    return useVendas({
        ...options,
        startDate: dataInicio,
        endDate: dataFim,
        sort: '-createdAt'
    }, {
        ...options,
        enabled: options.enabled !== false && !!dataInicio && !!dataFim
    });
};

// ✅ CORREÇÃO: Hook para vendas por cliente
export const useVendasPorCliente = (clienteId, options = {}) => {
    return useVendas({
        ...options,
        cliente: clienteId,
        sort: '-createdAt'
    }, {
        ...options,
        enabled: options.enabled !== false && !!clienteId
    });
};

// ✅ CORREÇÃO: Hook para vendas por profissional
export const useVendasPorProfissional = (profissionalId, options = {}) => {
    return useVendas({
        ...options,
        profissionalPrincipal: profissionalId,
        sort: '-createdAt'
    }, {
        ...options,
        enabled: options.enabled !== false && !!profissionalId
    });
};

// ✅ CORREÇÃO: Hook para vendas por origem
export const useVendasPorOrigem = (origem, options = {}) => {
    const queryParams = {};
    
    switch (origem) {
        case 'pedido':
            queryParams.pedidoOrigem = { $exists: true, $ne: null };
            break;
        case 'agendamento':
            queryParams.agendamentoOrigem = { $exists: true, $ne: null };
            break;
        case 'direta':
            queryParams.pedidoOrigem = null;
            queryParams.agendamentoOrigem = null;
            break;
        default:
            break;
    }
    
    return useVendas({
        ...options,
        ...queryParams,
        sort: '-createdAt'
    }, {
        ...options,
        enabled: options.enabled !== false && !!origem
    });
};

// ✅ CORREÇÃO: Hook para estatísticas básicas
export const useVendasEstatisticasBasicas = (periodo = '7d') => {
    const dataInicio = new Date();
    
    switch (periodo) {
        case 'hoje':
            dataInicio.setHours(0, 0, 0, 0);
            break;
        case '7d':
            dataInicio.setDate(dataInicio.getDate() - 7);
            break;
        case '30d':
            dataInicio.setDate(dataInicio.getDate() - 30);
            break;
        case '90d':
            dataInicio.setDate(dataInicio.getDate() - 90);
            break;
        default:
            dataInicio.setDate(dataInicio.getDate() - 7);
    }
    
    const { data: vendasData, isLoading, error } = useVendasPorPeriodo(
        dataInicio.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0],
        { enabled: true }
    );
    
    const vendasArray = Array.isArray(vendasData?.data) ? vendasData.data : [];
    
    const estatisticas = {
        totalVendas: vendasData?.count || 0,
        faturamentoTotal: vendasArray.reduce((sum, venda) => sum + (venda.total || 0), 0),
        vendasFinalizadas: vendasArray.filter(v => v.statusVenda === 'Finalizada').length,
        vendasPendentes: vendasArray.filter(v => v.statusVenda === 'Aberta').length,
        vendasCanceladas: vendasArray.filter(v => v.statusVenda === 'Cancelada').length,
        ticketMedio: 0,
        taxaConversao: 0,
        isLoading,
        error
    };
    
    // Cálculos seguros
    if (estatisticas.vendasFinalizadas > 0) {
        estatisticas.ticketMedio = estatisticas.faturamentoTotal / estatisticas.vendasFinalizadas;
    }
    
    if (estatisticas.totalVendas > 0) {
        estatisticas.taxaConversao = (estatisticas.vendasFinalizadas / estatisticas.totalVendas) * 100;
    }
    
    return estatisticas;
};

// ✅ CORREÇÃO: Hook para relatórios de vendas
export const useRelatoriosVendas = (filtros = {}) => {
    return useQuery({
        queryKey: ['relatorios', 'vendas', filtros],
        queryFn: async () => {
            try {
                console.log('🔄 [useRelatoriosVendas] Buscando relatórios:', filtros);
                
                const response = await apiClient.get('/vendas/relatorios/geral', {
                    params: filtros,
                    timeout: 30000
                });
                
                return response.data;
            } catch (error) {
                console.error('❌ [useRelatoriosVendas] Erro ao buscar relatórios:', error);
                
                if (error.response?.status === 500) {
                    throw new Error('Serviço de relatórios temporariamente indisponível');
                }
                
                throw error;
            }
        },
        enabled: !!filtros.startDate && !!filtros.endDate,
        staleTime: 10 * 60 * 1000,
        cacheTime: 15 * 60 * 1000,
        retry: 1
    });
};

// ✅ CORREÇÃO: Hook para relatórios de comissões
export const useRelatoriosComissoes = (filtros = {}) => {
    return useQuery({
        queryKey: ['relatorios', 'comissoes', filtros],
        queryFn: async () => {
            try {
                console.log('🔄 [useRelatoriosComissoes] Buscando relatórios de comissões:', filtros);
                
                const response = await apiClient.get('/vendas/relatorios/comissoes', {
                    params: filtros,
                    timeout: 30000
                });
                
                return response.data;
            } catch (error) {
                console.error('❌ [useRelatoriosComissoes] Erro ao buscar relatórios de comissões:', error);
                
                if (error.response?.status === 500) {
                    throw new Error('Serviço de comissões temporariamente indisponível');
                }
                
                throw error;
            }
        },
        enabled: !!filtros.startDate && !!filtros.endDate,
        staleTime: 10 * 60 * 1000,
        cacheTime: 15 * 60 * 1000,
        retry: 1
    });
};

// ✅ NOVO: Hook com fallback para erro 500
export const useVendasWithFallback = (params = {}, options = {}) => {
    const query = useVendas(params, options);
    
    const shouldUseFallback = 
        query.error && 
        (query.error.response?.status === 500 || query.isError);
    
    return {
        ...query,
        data: shouldUseFallback ? 
            (query.data || { 
                success: false, 
                data: [], 
                count: 0, 
                totalCount: 0, 
                pagination: { page: 1, limit: 10, totalPages: 0, totalItems: 0 }, 
                total: 0 
            }) : 
            query.data,
        isFallback: shouldUseFallback,
        isLoading: !shouldUseFallback && query.isLoading
    };
};

// ✅ NOVO: Hook para verificar saúde da API
export const useApiHealth = () => {
    return useQuery({
        queryKey: ['api-health'],
        queryFn: async () => {
            try {
                const response = await apiClient.get('/health', { timeout: 5000 });
                return {
                    status: 'healthy',
                    timestamp: new Date().toISOString(),
                    data: response.data
                };
            } catch (error) {
                return {
                    status: 'unhealthy',
                    error: error.message,
                    timestamp: new Date().toISOString(),
                    isServerError: error.response?.status === 500
                };
            }
        },
        refetchInterval: 30000,
        refetchOnWindowFocus: true,
        staleTime: 10000,
        retry: 1
    });
};

export default useVendas;